import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import {
  AiGeneratedQuestionLatex,
  ChapterWiseQuestionCounts,
  ContentChildrenInputs,
  ContentConfig,
  ContentData,
  ContentIds,
  CreateQrInputs,
  KSCClusterDetails,
  KSCDetails,
  TagLearnVideoInputs,
  PdfQuestion,
  QrCategoryProps,
  QrCodeContentDisplay,
  QuestionByChapterKSC,
  QuestionDetails,
  QuestionPdfInfo,
  QuestionReviewDisplay,
  SimilarQuestionPair,
  StudyMaterial,
  UpdateQrInputs,
} from "@/types/content";
import { ApiResponse, IdNameChildren, IdNamePair, OrderedIdNameChildren } from "@/types/global";
import { Section, SectionGroup, SelectorQuestion } from "@/types/questionSelector";
import { ChapterPdfContent } from "@/types/smartBooks";
import {
  DisplayConcept,
  DisplayConceptCluster,
  LearnVideo,
  LearnVideoDisplay,
  LearnVideoInputs,
  MarkedUserQuestionDisplay,
  UpdateLearnVideoInputs,
} from "@/types/studentDashboard";
export default class ContentApi extends ApiVerified {
  async getInstituteTestRecommendedQuestions(
    testSection: Section,
    exclusionBatchIds: Array<number> | null,
    difficultyLevelId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<SelectorQuestion>>(
        `institute-test/get-recommended-questions`,
        {
          testSection: testSection,
          exclusionBatchIds: exclusionBatchIds,
          difficultyLevelId: difficultyLevelId,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionTypesForContent(
    selectedContent: ContentIds,
    categoryId: number | null,
    minimumCount: number | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<number>>(
        `content/get-question-types-for-content`,
        { selectedContent: selectedContent, categoryId: categoryId, minimumCount: minimumCount },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionHtmlUrl(questionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<string>(`content/get-question-display-url`, {
        params: { questionId: questionId },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionDisplayUrl(
    questionId: number,
    displayType: string | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<string>(`content/get-question-display-url`, {
        params: { questionId: questionId, displayType: displayType },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionImageUrl(questionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<string>(`content/get-question-image`, {
        params: { questionId: questionId },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionHintsHtml(questionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<string>>(
        `content/get-question-hints-html`,
        { params: { questionId: questionId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSolutionImageUrl(questionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<string>(`content/get-question-solution-image`, {
        params: { questionId: questionId },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionConceptImages(questionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<string>>(
        `content/get-question-concept-images`,
        {
          params: { questionId: questionId },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionSolutionImage(questionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<string>(`content/get-question-solution-image`, {
        params: { questionId: questionId },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionDetails(questionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<QuestionDetails>(
        `content/get-question-details`,
        { params: { questionId: questionId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateQuestionDetails(updatedQuestion: QuestionDetails): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content/fix-question`,
        updatedQuestion,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveCourseChapterQuestionExclusion(
    questionId: number,
    courseChapterId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `content/save-course-chapter-question-exclusion`,
        {
          questionId: questionId,
          courseChapterId: courseChapterId,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionCountsForContent(selectedContent: ContentIds) {
    try {
      const response = await this.axiosInstance.post(`content/get-question-counts-for-content`, {
        selectedContent: selectedContent,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationUploadedQuestions(
    affiliationId: number,
    courseChapterId: number | null,
    questionTypeId: number | null,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SelectorQuestion>>(
        `content/get-affiliation-uploaded-questions`,
        {
          params: {
            affiliationId: affiliationId,
            courseChapterId: courseChapterId,
            questionTypeId: questionTypeId,
            onlyActive: onlyActive,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveAssignmentQuestions(
    assignmentId: number,
    affiliationId: number,
    sectionGroups: Array<SectionGroup>,
    finalizeAssignment: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `assignment/save-assignment-questions`,
        {
          assignmentId: assignmentId,
          affiliationId: affiliationId,
          sectionGroups: sectionGroups,
          finalizeAssignment: finalizeAssignment,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAvailableAssignmentQuestionsForChapter(
    courseChapterId: number,
    kscClusterIds: Array<number> | null,
    questionTypeId: number,
    onlyActive: boolean,
    exclusionBatchIdsCsv: string | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<SelectorQuestion>>(
        `assignment/get-available-questions-for-chapter`,
        {
          courseChapterId: courseChapterId,
          kscClusterIds: kscClusterIds,
          questionTypeId: questionTypeId,
          onlyActive: onlyActive,
          exclusionBatchIdsCsv: exclusionBatchIdsCsv,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAssignmentRecommendedQuestions(
    assignmentSection: Section,
    exclusionBatchIds: Array<number> | null,
    difficultyLevelId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<SelectorQuestion>>(
        `assignment/get-recommended-questions`,
        {
          assignmentSection: assignmentSection,
          exclusionBatchIds: exclusionBatchIds,
          difficultyLevelId: difficultyLevelId,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async executeQuestionHtmlApi(questionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`content/execute-question-html-api`, {
        questionId: questionId,
        runInContainer: true,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async submitQuestionPdf(
    pdfFileName: string,
    pdfContent: string,
    courseId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`content/submit-question-pdf`, {
        pdfFileName: pdfFileName,
        pdfContent: pdfContent,
        courseId: courseId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPdfQuestionsForCategoryTagging(pdfUUID: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<PdfQuestion>>(
        `content/get-pdf-questions-for-category-tagging`,
        { params: { pdfUUID: pdfUUID } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
  async submitCategoryTaggedQuestions(questions: Array<PdfQuestion>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content/submit-category-tagged-questions`,
        questions,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
  async getPdfQuestionsForReview(pdfUUID: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<QuestionReviewDisplay>>(
        `content/get-pdf-questions-for-review`,
        { params: { pdfUUID: pdfUUID } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
  async saveReviewedQuestions(questions: Array<QuestionReviewDisplay>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content/save-reviewed-questions`,
        questions,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async loadAllQuestionPdfs(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<QuestionPdfInfo>>(
        `content/get-all-question-pdfs`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllCourseChapters(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content/get-all-course-chapters`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCourseChaptersForBatch(
    batchId: number,
    subjectId: number,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `affiliation-batch/get-chapters-for-batch`,
        { params: { batchId: batchId, subjectId: subjectId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSelectedContent(
    courseChapterIds: Array<number>,
    kscClusterIds: Array<number> | null,
    contentConfig: Array<ContentConfig>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ContentIds>(`content/selected-content`, {
        courseChapterIds: courseChapterIds,
        kscClusterIds: kscClusterIds,
        contentConfig: contentConfig,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getKSCClustersForCourseChapter(
    courseChapterId: number,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content/get-ksc-clusters-for-course-chapter`,
        { params: { courseChapterId: courseChapterId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getKSCsForKSCCluster(kscClusterId: number, onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content/get-kscs-for-ksc-cluster`,
        { params: { kscClusterId: kscClusterId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getNestedKSCsForChapter(
    courseChapterId: number,
    onlyActive: boolean,
    includeKSCCode: boolean = false,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNameChildren>>(
        `content/nested-kscs-for-chapter`,
        {
          params: {
            courseChapterId: courseChapterId,
            includeKSCCode: includeKSCCode,
            onlyActive: onlyActive,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getKSCClusterDetailsByCode(kscClusterCode: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<KSCClusterDetails>(
        `content/get-ksc-cluster-details-by-code`,
        { params: { kscClusterCode: kscClusterCode } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getKSCDetails(kscId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<KSCDetails>(`content/get-ksc-details`, {
        params: { kscId: kscId },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getKSCDetailsByCode(kscCode: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<KSCDetails>(`content/get-ksc-details-by-code`, {
        params: { kscCode: kscCode },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveKSCDetails(kscDetails: KSCDetails): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `content/save-ksc-details`,
        kscDetails,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCommonChaptersForBatches(batchIdsCsv: string, subjectId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `affiliation-batch/get-common-chapters`,
        { params: { batchIdsCsv: batchIdsCsv, subjectId: subjectId, onlyActive: true } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCourseChaptersMapping(
    courseId: number,
    includeTopics: boolean,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNameChildren>>(
        `content/get-course-chapters-mapping`,
        { params: { courseId: courseId, includeTopics: includeTopics, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getConceptClustersForChapter(
    userId: number,
    courseChapterId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<DisplayConceptCluster>>(
        `/learn/get-concept-clusters`,
        { params: { userId: userId, courseChapterId: courseChapterId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getConceptsForConceptCluster(
    courseChapterId: number,
    kscClusterId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<DisplayConcept>>(`/learn/get-concepts`, {
        params: { courseChapterId: courseChapterId, kscClusterId: kscClusterId },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getLearnVideosForChapter(
    courseChapterId: number,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<LearnVideoDisplay>>(
        `/learn/get-learn-videos-for-chapter`,
        { params: { courseChapterId: courseChapterId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async addLearnVideo(inputData: LearnVideoInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`learn/add-learn-video`, inputData);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async searchLearnVideosByName(searchText: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<LearnVideo>>(
        `/learn/search-learn-videos-by-name`,
        { params: { searchText: searchText } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async tagLearnVideoToChapter(inputData: TagLearnVideoInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `learn/tag-learn-video-to-chapter`,
        inputData,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateLearnVideo(updatedVideo: UpdateLearnVideoInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `learn/update-learn-video`,
        updatedVideo,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateLearnVideoTagsForChapter(
    learnVideos: Array<LearnVideoDisplay>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `learn/update-learn-video-tags-for-chapter`,
        learnVideos,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getContentNames(contentIds: Array<number>, contentType: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<IdNamePair>>(`content/content-names`, {
        contentIds: contentIds,
        contentType: contentType,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getContentChildren(contentChildrenInputs: ContentChildrenInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<ContentData>>(
        `content/content-children`,
        contentChildrenInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getNestedContentChildren(
    contentChildrenInputs: ContentChildrenInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<ContentData>>(
        `content/nested-content-children`,
        contentChildrenInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllContentByType(contentType: string, onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content/get-all-content-by-type`,
        { params: { contentType: contentType, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSubjectsForStudent(userId: number, onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content/get-subjects-for-student`,
        { params: { userId: userId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveCourseChapterMapping(courseChapters: OrderedIdNameChildren): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `content/save-course-chapter-mapping`,
        { courseChapters: courseChapters },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveKSCClustersForCourseChapter(
    courseChapterId: number,
    kscClusters: Array<OrderedIdNameChildren>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `content/save-ksc-clusters-for-course-chapter`,
        { courseChapterId: courseChapterId, kscClusters: kscClusters },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveKSCClusterKSCMapping(
    courseChapterId: number,
    kscClusterKscMapping: Array<OrderedIdNameChildren>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `content/save-ksc-cluster-ksc-mapping`,
        { courseChapterId: courseChapterId, kscClusterKscMapping: kscClusterKscMapping },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createNewPdfUUID(courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<string>(`content/create-new-pdf-uuid`, {
        courseId: courseId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCourseSubjects(courseId: number, onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content/get-course-subjects`,
        { params: { courseId: courseId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSubjectsForBatch(batchId: number, onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `affiliation-batch/get-subjects-for-batch`,
        { params: { batchId: batchId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCommonSubjectsForBatches(batchIdsCsv: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `affiliation-batch/get-common-subjects`,
        { params: { batchIdsCsv: batchIdsCsv, onlyActive: true } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCoursesForAffiliation(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `affiliation-batch/get-affiliation-courses`,
        { params: { affiliationId: affiliationId, onlyActive: true } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getClassesForStudent(userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content/get-classes-for-student`,
        { params: { userId: userId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudyMaterialForStudent(userId: number, courseChapterId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<StudyMaterial>>(
        `/learn/get-study-material-for-student`,
        { params: { userId: userId, courseChapterId: courseChapterId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getMarkedUserQuestions(
    userId: number,
    courseId: number,
    classId: number,
    subjectId: number,
    markedCategoryId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<MarkedUserQuestionDisplay>>(
        `learn/get-marked-user-questions`,
        {
          params: {
            userId: userId,
            courseId: courseId,
            classId: classId,
            subjectId: subjectId,
            markedCategoryId: markedCategoryId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async unmarkUserQuestion(
    userId: number,
    questionId: number,
    markedCategoryId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(`learn/unmark-user-question`, {
        userId: userId,
        questionId: questionId,
        markedCategoryId: markedCategoryId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQrCategoryProps(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<{ [categoryId: number]: QrCategoryProps }>(
        `content/get-qr-category-props`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createQrCode(qrCreateInputs: CreateQrInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<CreateQrInputs>(
        `content/create-qr-code`,
        qrCreateInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveQrCodeContent(qrCodeContentInputs: CreateQrInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `content/save-qr-code-content`,
        qrCodeContentInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQrCodeDetails(qrCode: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<QrCodeContentDisplay>(
        `content/get-qr-code-details`,
        { params: { qrCode: qrCode } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQrCodeDetailsByShortCode(qrShortCode: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<QrCodeContentDisplay>(
        `content/get-qr-code-details-by-short-code`,
        { params: { qrShortCode: qrShortCode } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDeprecatedQrCode(deprecatedUrl: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<QrCodeContentDisplay>(
        `content/get-deprecated-qr-code`,
        { deprecatedUrl: deprecatedUrl },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllQrCodeDetails(qrCodes: Array<string>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<QrCodeContentDisplay>>(
        `content/get-all-qr-code-details`,
        { qrCodes },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCourseWiseQuestionCounts(
    courseId: number,
    classId: number,
    subjectId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ChapterWiseQuestionCounts>>(
        `content/get-course-wise-question-counts`,
        { params: { courseId: courseId, classId: classId, subjectId: subjectId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getChapterQuestionsForReview(courseChapterId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<QuestionByChapterKSC>>(
        `content/get-chapter-questions-for-review`,
        { params: { courseChapterId: courseChapterId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQrCodesForCategory(
    qrCategoryId: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<QrCodeContentDisplay>>(
        `content/get-qr-codes-for-category`,
        { params: { qrCategoryId: qrCategoryId, pageNumber: pageNumber, pageSize: pageSize } },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQrCodesForChapter(
    courseChapterId: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<QrCodeContentDisplay>>(
        `content/get-qr-codes-for-chapter`,
        {
          params: { courseChapterId: courseChapterId, pageNumber: pageNumber, pageSize: pageSize },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateQrCodeContent(updateQrInputs: UpdateQrInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `content/update-qr-code-content`,
        updateQrInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteQrCodeContent(qrCode: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(`content/delete-qr-code-content`, {
        params: { qrCode: qrCode },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async runCustomOpenAiPrompt(systemMessage: string, promptBody: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<any>(`/open-ai/run-custom-open-ai-prompt`, {
        params: { systemMessage: systemMessage, promptBody: promptBody },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAiMarkedSimilarQuestions(courseChapterId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SimilarQuestionPair>>(
        `/content/get-ai-marked-similar-questions`,
        { params: { courseChapterId: courseChapterId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateReviewedSimilarQuestions(
    questionPairs: Array<SimilarQuestionPair>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `/content/update-reviewed-similar-questions`,
        questionPairs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async searchContentByName(contentType: string, searchText: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ContentData>>(
        `content/search-content-by-name`,
        { params: { contentType: contentType, searchText: searchText } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
  async extractPdfChapter(pdfFileName: string, pdfContent: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`content/extract-pdf-chapter`, {
        pdfFileName: pdfFileName,
        pdfContent: pdfContent,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getChapterPdfContent(pdfUUID: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ChapterPdfContent>(
        `content/get-chapter-pdf-content`,
        {
          params: { pdfUUID: pdfUUID },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateChapterPdfContent(
    pdfUUID: string,
    chapterPdfContent: ChapterPdfContent,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `content/update-chapter-pdf-content`,
        {
          pdfUUID: pdfUUID,
          chapterPdfContent: chapterPdfContent,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
